import React, { useState, useEffect, useContext } from 'react';
import { Loader } from '@/components/custom/loader';
import { useSession } from 'src/app/hooks/useSession';
import useAuthorityLevel from '@/app/hooks/useAuthorityLevel';
import { ProjectsProvider } from './provider';
import { ProjectsContext } from './provider';
import { ProjectsCard } from './projects-card';
import { ProjectForm } from './ProjectForm';
import { EmptyState } from '@/components/custom/empty-state';
import Onboarding from 'src/app/components/Onboarding';
import usePermission from '@/app/hooks/usePermission';
import { useFormik } from 'formik';
import { FilterSelectMulti } from '@/components/custom/filter/filter-select-multi';
import { Filter, FilterActions, FilterContent, FilterInput, FilterSelectNative } from '@/components/custom/filter';
import { useClient } from 'jsonapi-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DashboardContent } from '@/layouts/dashboard';
import { FiSearch } from 'react-icons/fi';
import { CardWrapper } from '@/components/ui/card';
import { Sheet, SheetBody, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';

export const Projects = props => (
  <ProjectsProvider>
    <ScreenContentProjects {...props} />
  </ProjectsProvider>
);

const ScreenContentProjects = props => {
  const navigate = useNavigate();
  const { uri } = props;

  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const path = props.path;
  const { session } = useSession();
  const client = useClient();
  const profiles = session?.user?.profileNames || (session?.user && session?.user['profile-names']) || [];
  const [relationships, setRelationships] = useState({
    courses: [],
    classrooms: []
  });

  const authorityLevel = useAuthorityLevel();

  const { loading, projects, getProjects } = useContext(ProjectsContext);

  const filters = useFormik({
    initialValues: {
      course: 'blank',
      classroom: 'blank',
      visibility: {
        approved: false,
        ongoing: false
      }
    }
  });

  const getRelationships = async () => {
    const { data: courses } = await client.fetch('courses');
    const { data: classrooms } = await client.fetch('classrooms');

    setRelationships({
      courses: courses || [],
      classrooms: classrooms || []
    });
  };

  function filterProjects(id, type, projects) {
    return projects.filter(function (project) {
      return project['projectable-type'] === type && project['projectable-id'] === id;
    });
  }

  function filterApproved(projects) {
    return projects.filter(function (project) {
      return project.approved === true;
    });
  }

  function filterOngoing(projects) {
    return projects.filter(function (project) {
      return project.approved === null;
    });
  }

  useEffect(() => {
    let newProjects = projects;
    if (filters.values.course !== 'blank') {
      const id = +filters.values.course;
      const type = 'Course';
      newProjects = filterProjects(id, type, projects);
    }
    if (filters.values.classroom !== 'blank') {
      const id = +filters.values.classroom;
      const type = 'Classroom';
      newProjects = filterProjects(id, type, projects);
    }
    if (searchTerm !== '') {
      newProjects = newProjects.filter(p => p.name != null && p.name.toLowerCase().includes(searchTerm.toLocaleLowerCase()));
    }
    if (filters.values.visibility.approved === true) {
      newProjects = filterApproved(newProjects);
    }
    if (filters.values.visibility.ongoing === true) {
      newProjects = filterOngoing(newProjects);
    }
    setFilteredProjects(newProjects);
  }, [filters.values, searchTerm, projects]);

  useEffect(() => {
    setFilteredProjects(projects);
    filters.setFieldValue('course', 'blank');
    filters.setFieldValue('classroom', 'blank');
  }, [projects]);

  useEffect(() => {
    getProjects();
    getRelationships();
  }, []);

  useEffect(() => {
    if (authorityLevel !== 'student') {
      navigate('/projetos/grupos');
    }
  }, [authorityLevel]);

  const breadcrumbs = {
    title: 'Atividades',
    nav: [
      {
        route: uri,
        name: 'Projetos',
        isActive: true
      }
    ]
  };

  const statusOptions = [
    {
      label: t('exams.statusInApproved'),
      value: filters.values.visibility.approved,
      onChange: () => {
        filters.setFieldValue('visibility', {
          approved: !filters.values.visibility.approved,
          ongoing: false
        });
      }
    },
    {
      label: t('filter.labelInProgress'),
      value: filters.values.visibility.ongoing,
      onChange: () => {
        filters.setFieldValue('visibility', {
          approved: false,
          ongoing: !filters.values.visibility.ongoing
        });
      }
    }
  ];

  if (!usePermission('Visualizar lista de projetos', 'Projetos')) {
    return null;
  }

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      <Filter>
        <FilterContent>
          <FilterInput
            label={t('materials.search')}
            endIcon={FiSearch}
            placeholder={t('filter.searchProject')}
            type="search"
            name="projectsSearch"
            id="projectsSearch"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          <FilterSelectNative
            label={t('filter.labelSelectCourse')}
            value={filters.values.course}
            onChange={e => {
              filters.setFieldValue('course', e.target.value);
              filters.setFieldValue('classroom', 'blank');
            }}
            options={relationships.courses.map(c => ({
              ...c,
              name: c.title
            }))}
          />
          <FilterSelectNative
            label={t('filter.labelSelectClassroom')}
            value={filters.values.classroom}
            onChange={e => {
              filters.setFieldValue('classroom', e.target.value);
              filters.setFieldValue('course', 'blank');
            }}
            options={relationships.classrooms.map(c => ({
              ...c,
              name: c.title
            }))}
          />
          <FilterSelectMulti data={statusOptions} />
        </FilterContent>

        {usePermission('Criar projetos', 'Projetos') && (
          <FilterActions>
            <Button
              wide
              type="button"
              onClick={() => setShow(true)}
            >
              {t('projects.newProjects')}
            </Button>
          </FilterActions>
        )}
      </Filter>

      {loading && <Loader />}

      {!loading && !filteredProjects.length > 0 && (
        <EmptyState
          type="data"
          title={t('emptyState.inAnyProjectTitle')}
          text={t('emptyState.inAnyProjectText')}
        />
      )}

      {!loading && filteredProjects.length > 0 && (
        <CardWrapper>
          {filteredProjects.map(p => (
            <ProjectsCard
              data={p}
              key={p.id}
            />
          ))}
        </CardWrapper>
      )}

      <Sheet
        open={show}
        onOpenChange={setShow}
      >
        <SheetContent>
          <SheetTitle className="sr-only">Novo Projeto</SheetTitle>
          <SheetBody className="py-6">
            <ProjectForm
              labelForm={'Criar novo projeto'}
              labelFormButton={t('button.saveContinue')}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>

      <Onboarding
        path={path}
        show={showOnboarding}
        setShowOnboarding={setShowOnboarding}
        onClose={() => setShowOnboarding(false)}
        profiles={profiles}
        title={t('onboarding.titleProjects')}
        videoId="_RHp2GVpIj4"
      />
    </DashboardContent>
  );
};
